<template>
  <div class="race-list-container">
    <div v-if="isLoading" class="race-list-loading">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="sr-only">{{ copy.loading }}</span>
      </div>
      <span class="ml-2">{{ copy.loading }}</span>
    </div>

    <div v-else-if="races.length === 0" class="race-list-empty">
      <p class="text-muted">{{ emptyMessage || copy.noResults }}</p>
    </div>

    <div v-else class="race-list">
      <div class="race-list-header p-2 bg-light border-bottom">
        <small class="text-muted font-weight-bold">
          {{ races.length }} {{ races.length === 1 ? 'race' : 'races' }} found
        </small>
      </div>

      <div class="race-list-items">
        <div
          v-for="race in races"
          :key="race.id"
          class="race-item"
          :class="{ 'race-item-selected': race.id === selectedRaceId }"
          @mouseenter="$emit('race-hover', race.id)"
          @mouseleave="$emit('race-hover', null)"
          @click="$emit('race-click', race.id)"
        >
          <div class="race-item-header">
            <h6 class="race-item-title mb-1">{{ race.event.name }}</h6>
            <small class="text-muted">{{ race.name }}</small>
          </div>

          <div class="race-item-details mt-2">
            <div class="race-item-detail">
              <i class="fas fa-map-marker-alt text-muted mr-1"></i>
              <small>{{ race.event.location }}</small>
            </div>

            <div class="race-item-detail">
              <i class="fas fa-calendar text-muted mr-1"></i>
              <small>{{ race.date }}</small>
            </div>

            <div v-if="race.route" class="race-item-detail">
              <i class="fas fa-route text-muted mr-1"></i>
              <small>{{ formatDistance(race.route.distance) }}</small>
            </div>

            <div v-if="race.route && race.route.elevation_gain" class="race-item-detail">
              <i class="fas fa-mountain text-muted mr-1"></i>
              <small>{{ formatElevation(race.route.elevation_gain) }}</small>
            </div>
          </div>

          <div v-if="race.route && race.route.terrain" class="race-item-tags mt-2">
            <span class="badge badge-secondary badge-sm">{{ race.route.terrain }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    races: {
      type: Array,
      default: () => []
    },
    selectedRaceId: {
      type: Number,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    useMetric: {
      type: Boolean,
      default: true
    },
    copy: {
      type: Object,
      default: () => ({
        loading: 'Loading...',
        noResults: 'No races found'
      })
    },
    emptyMessage: {
      type: String,
      default: null
    }
  },

  methods: {
    formatDistance(distanceInMeters) {
      if (!distanceInMeters) return '-';

      if (this.useMetric) {
        return `${(distanceInMeters / 1000).toFixed(1)} km`;
      } else {
        return `${(distanceInMeters * 0.000621371).toFixed(1)} mi`;
      }
    },

    formatElevation(elevationInMeters) {
      if (!elevationInMeters) return '-';

      if (this.useMetric) {
        return `${Math.round(elevationInMeters)} m`;
      } else {
        return `${Math.round(elevationInMeters * 3.28084)} ft`;
      }
    }
  }
};
</script>

<style scoped>
.race-list-container {
  max-height: calc(100vh - 400px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.race-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.race-list-loading,
.race-list-empty {
  padding: 2rem 1.5rem;
  text-align: center;
}

.race-list-empty p {
  margin: 0;
}

.race-list-items {
  overflow-y: auto;
  flex: 1;
}

.race-item {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.race-item:hover {
  background-color: #f8f9fa;
}

.race-item-selected {
  background-color: #e7f3ff;
  border-left: 3px solid #6B46C1;
}

.race-item-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.3;
}

.race-item-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.race-item-detail {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
}

.race-item-detail i {
  width: 16px;
  font-size: 0.75rem;
}

.race-item-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Custom scrollbar */
.race-list-items::-webkit-scrollbar {
  width: 8px;
}

.race-list-items::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.race-list-items::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.race-list-items::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
